import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { MsalService } from '@azure/msal-angular';
import { AppStateHolder } from '../shared/app-state.holder';
import { clearAuthSessionFlags } from '../shared/auth-cleanup';
import { DebugLoggerService } from './debug-logger.service';
import { SessionExpiredService } from './session-expired.service';
import { environment } from '../../environments/environment';

@Injectable({ providedIn: 'root' })
export class AuthSessionService {
  private static readonly COOLDOWN_MS = 5000;
  private static readonly LOG_PREFIX = '[AmSuite Auth]';

  private _router = inject(Router);
  private _appStateHolder = inject(AppStateHolder);
  private _msalService = inject(MsalService);
  private _debugLogger = inject(DebugLoggerService);
  private _sessionExpired = inject(SessionExpiredService);

  /** Durata countdown overlay prima del redirect a /login (secondi). */
  private static readonly SESSION_EXPIRED_COUNTDOWN_S = 4;

  private _reauthInProgress = false;
  private _lastReauthAttempt = 0;

  isReauthInProgress(): boolean {
    return this._reauthInProgress;
  }

  /**
   * Punto unico per sessione scaduta: silent refresh → redirect a /login (nuovo login).
   * Nessun popup MSAL: su tenant B2C federati via HRD policy verso AAD del cliente il
   * popup può fallire con "Non è stato possibile completare l'accesso" (cookie cross-site
   * bloccati, conditional access, session AAD scaduta). loginRedirect full-page dalla
   * pagina /login evita il problema perché la federazione avviene in main frame.
   * Mutex + cooldown evitano loop da 401 multipli / eventi MSAL concorrenti.
   */
  async reauthenticate(reason: string): Promise<boolean> {
    if (this._reauthInProgress) {
      this.log('reauthenticate skipped — already in progress', { reason });
      return false;
    }
    if (Date.now() - this._lastReauthAttempt < AuthSessionService.COOLDOWN_MS) {
      this.log('reauthenticate skipped — cooldown active', {
        reason,
        cooldownRemainingMs: AuthSessionService.COOLDOWN_MS - (Date.now() - this._lastReauthAttempt),
      });
      return false;
    }

    this._reauthInProgress = true;
    this._lastReauthAttempt = Date.now();
    this.log('reauthenticate started', { reason, routerUrl: this._router.url });

    try {
      const silentToken = await this.attemptSilentRefresh();
      if (silentToken) {
        this.syncTokenToCookies(silentToken);
        this.log('reauthenticate succeeded via silent refresh');
        return true;
      }

      if (this.isOnLoginRoute()) {
        this.log('reauthenticate skipped — already on /login route', {
          reason,
          routerUrl: this._router.url,
        });
        return false;
      }

      this.log('reauthenticate — silent refresh failed, clearing session and navigating to /login');
      await this.clearSessionAndGoToLogin();
      return false;
    } finally {
      this._reauthInProgress = false;
    }
  }

  /** Alias per i call site esistenti. */
  async handleSessionExpired(reason: string): Promise<void> {
    await this.reauthenticate(reason);
  }

  async attemptSilentRefresh(): Promise<string | null> {
    const account = this._msalService.instance.getActiveAccount()
      ?? this._msalService.instance.getAllAccounts()[0];
    if (!account) {
      this.log('attemptSilentRefresh — no MSAL account');
      return null;
    }

    try {
      this.log('attemptSilentRefresh — forceRefresh', {
        account: account.username,
        accountsTotal: this._msalService.instance.getAllAccounts().length,
      });
      const result = await this._msalService.instance.acquireTokenSilent({
        scopes: ['openid', 'profile', 'offline_access', environment.msalApiScope],
        account,
        forceRefresh: true,
      });
      const hasToken = !!result?.accessToken;
      this.log('attemptSilentRefresh — result', { hasToken });
      return result?.accessToken || null;
    } catch (error: unknown) {
      const err = error as { name?: string; message?: string };
      this.log('attemptSilentRefresh — failed', {
        errorName: err?.name ?? null,
        errorMessage: err?.message ?? null,
      });
      return null;
    }
  }

  private syncTokenToCookies(accessToken: string): void {
    this._appStateHolder.setCookie('authType', 'b2c');
    this._appStateHolder.setCookie('authToken', accessToken);
  }

  private clearAuthCookies(): void {
    this._appStateHolder.removeCookie('authType');
    this._appStateHolder.removeCookie('authToken');
    this._appStateHolder.removeCookie('authTypeDomain');
    this._appStateHolder.removeCookie('ImpersonateState');
    this._appStateHolder.removeCookie('ImpersonationSessionId');
  }

  private async clearSessionAndGoToLogin(): Promise<void> {
    this.clearAuthCookies();
    clearAuthSessionFlags();

    const accountsBefore = this._msalService.instance.getAllAccounts().length;
    try {
      await this._msalService.instance.clearCache();
    } catch {
      this._msalService.instance.setActiveAccount(null);
    }
    const accountsAfter = this._msalService.instance.getAllAccounts().length;
    this.log('clearSessionAndGoToLogin — MSAL cache cleared', { accountsBefore, accountsAfter });

    if (!this.isOnLoginRoute()) {
      // Cinematic overlay: comunica all'utente cosa sta succedendo e fa
      // contare il countdown prima di rimandarlo alla login. CTA accelera.
      await this._sessionExpired.show(AuthSessionService.SESSION_EXPIRED_COUNTDOWN_S);
      await this._router.navigate(['/login']);
    }
  }

  private isOnLoginRoute(): boolean {
    return this._router.url === '/login' || this._router.url.startsWith('/login/');
  }

  private log(message: string, data?: Record<string, unknown>): void {
    if (data !== undefined) {
      console.log(`${AuthSessionService.LOG_PREFIX} ${message}`, data);
    } else {
      console.log(`${AuthSessionService.LOG_PREFIX} ${message}`);
    }
    this._debugLogger.logAction('AuthSessionService', message, data);
  }
}
